import React, { useEffect, useState } from 'react';

import { ExclamationCircleOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Descriptions,
  Input,
  Modal as ModalAntD,
} from 'antd';
import moment from 'moment';
import styled from 'styled-components';

import {
  ManualReconcileResponse,
  RECONCILIATION_STATUS_META,
  manualReconcile,
  manualUndo,
} from '../../../hooks/reconciliation';

const Modal: any = ModalAntD;
const { TextArea }: any = Input;

const REASON_MAX = 255;

const InfoBox = styled.div`
  background: #fafafa;
  border: 1px solid #f0f0f0;
  border-radius: 4px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const CharCounter = styled.div<{ warn: boolean }>`
  text-align: right;
  font-size: 12px;
  color: ${p => (p.warn ? '#ff4d4f' : '#8c8c8c')};
  margin-top: 4px;
`;

export type ManualMode = 'reconcile' | 'undo';

export interface ManualTargetRow {
  transactionPk: number;
  referencePeriod: string;   // 'YYYY-MM'
  autoStatus: string | null;
  effectiveStatus: string | null;
  manualReconciledAt?: string | null;
  manualReason?: string | null;
  manualReconciledByUserPk?: number | null;
}

interface Props {
  visible: boolean;
  mode: ManualMode;
  target: ManualTargetRow | null;
  onClose: () => void;
  onDone?: (response: ManualReconcileResponse) => void;
}

const ReconciliationManualModal: React.FC<Props> = ({
  visible,
  mode,
  target,
  onClose,
  onDone,
}) => {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<ManualReconcileResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!visible) {
      setReason('');
      setSubmitting(false);
      setSuccess(null);
      setError(null);
    }
  }, [visible]);

  if (!target) return null;

  const isReconcile = mode === 'reconcile';
  const reasonTrimmed = reason.trim();
  const reasonValid =
    isReconcile ? reasonTrimmed.length >= 1 && reason.length <= REASON_MAX : true;
  const canSubmit = reasonValid && !submitting && !success;

  const handleClose = () => {
    const successResp = success;
    onClose();
    if (successResp && onDone) onDone(successResp);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const resp = isReconcile
        ? await manualReconcile(target.transactionPk, target.referencePeriod, reasonTrimmed)
        : await manualUndo(target.transactionPk, target.referencePeriod);
      setSuccess(resp);
    } catch (e: any) {
      const data = e?.response?.data;
      const msg = data?.message || e?.message || 'Falha ao processar.';
      const details = data?.details ? ` (${JSON.stringify(data.details)})` : '';
      setError(`${msg}${details}`);
    } finally {
      setSubmitting(false);
    }
  };

  const title = isReconcile
    ? `Conciliar manualmente — recarga #${target.transactionPk}`
    : `Desfazer conciliação manual — recarga #${target.transactionPk}`;

  const okButton = isReconcile ? 'Confirmar conciliação' : 'Confirmar desfazer';
  const autoMeta =
    target.autoStatus && RECONCILIATION_STATUS_META[target.autoStatus];

  return (
    <Modal
      title={title}
      visible={visible}
      width={580}
      maskClosable={false}
      destroyOnClose
      onCancel={handleClose}
      footer={
        success ? (
          <Button type='primary' onClick={handleClose}>
            Fechar e atualizar listagem
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type='primary'
              danger={!isReconcile}
              onClick={handleSubmit}
              disabled={!canSubmit}
              loading={submitting}
            >
              {okButton}
            </Button>
          </>
        )
      }
    >
      <InfoBox>
        <Descriptions column={1} size='small' colon>
          <Descriptions.Item label='Competência'>
            {target.referencePeriod}
          </Descriptions.Item>
          <Descriptions.Item label='Status automático'>
            {autoMeta ? autoMeta.label : target.autoStatus || '—'}
          </Descriptions.Item>
          {!isReconcile && target.manualReason && (
            <>
              <Descriptions.Item label='Motivo registrado'>
                {target.manualReason}
              </Descriptions.Item>
              <Descriptions.Item label='Conciliado em'>
                {target.manualReconciledAt
                  ? moment(target.manualReconciledAt).format('DD/MM/YYYY HH:mm')
                  : '—'}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>
      </InfoBox>

      {isReconcile && !success && (
        <>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>
            Motivo / evidência <span style={{ color: '#ff4d4f' }}>*</span>
          </div>
          <TextArea
            rows={4}
            maxLength={REASON_MAX}
            placeholder='Ex: Comprovante PIX externo em anexo #123; cliente confirmou pagamento por fora do PagBank.'
            value={reason}
            onChange={(e: any) => setReason(e.target.value)}
            disabled={submitting}
          />
          <CharCounter warn={reason.length === REASON_MAX}>
            {reason.length}/{REASON_MAX}
          </CharCounter>
        </>
      )}

      {!isReconcile && !success && (
        <Alert
          type='warning'
          icon={<ExclamationCircleOutlined />}
          showIcon
          message='Desfazer conciliação manual'
          description={
            <span>
              A linha voltará ao status automático{' '}
              <strong>
                {autoMeta ? autoMeta.label : target.autoStatus || '—'}
              </strong>
              . O motivo registrado será removido, mas a trilha de auditoria permanece.
            </span>
          }
        />
      )}

      {error && (
        <Alert
          type='error'
          message='Falha ao processar'
          description={error}
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {success && (
        <Alert
          type='success'
          showIcon
          message={
            isReconcile
              ? 'Conciliação manual registrada.'
              : 'Conciliação manual desfeita.'
          }
          description={`Status efetivo agora: ${
            RECONCILIATION_STATUS_META[success.effective_status]?.label ||
            success.effective_status
          }`}
        />
      )}
    </Modal>
  );
};

export default ReconciliationManualModal;
